import {
  DRYDOCK_TRADES,
  type DrydockJobStatus,
  PDI_DISPOSITIONS,
  PDI_FINDING_PRIORITIES,
  type PdiFindingStatus,
} from '@felix/domain';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Group,
  Modal,
  NumberInput,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconBan,
  IconCheck,
  IconEdit,
  IconEye,
  IconLink,
  IconPlus,
  IconSearch,
  IconSend,
  IconTool,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import classes from './Drydock.module.css';
import {
  ExecutionBadge,
  FindingClosurePanel,
  JobExecutionPanel,
  VerificationBadge,
} from './ExecutionPanels.js';
import { useDrydockErrorText } from './errors.js';
import type { DrydockDecisionInput, DrydockScopeSource } from './source.js';
import type {
  DrydockJobInput,
  DrydockJobRow,
  DrydockOriginInput,
  DrydockReviewRow,
  DrydockScope,
  PdiDefectCandidate,
  PdiFindingInput,
  PdiFindingRow,
} from './types.js';

/**
 * Pre-Docking bulgu, iş ve bağ panelleri (ADR 0129 Ek Faz 3; Excel Screen Workflow 5–8, 11).
 * Gemide bulgu ve iş elle açılır (R4: sonuç hiçbirini açmaz), teknik incelemeden geçer, iş ofise
 * gönderilir; ofiste yalnız iş kararı verilir (Superintendent). Düğmeler `scope.can` ve kaynağın
 * işlemleriyle görünür; sunucu her yazmada yine denetler.
 */

export const FINDING_TONE: Record<PdiFindingStatus, StatusTone> = {
  draft: 'warning',
  reviewed: 'info',
  actioned: 'success',
  closing: 'warning',
  closed: 'success',
  cancelled: 'neutral',
};

export const JOB_TONE: Record<DrydockJobStatus, StatusTone> = {
  draft: 'warning',
  for_review: 'info',
  approved: 'success',
  returned: 'danger',
  rejected: 'danger',
  cancelled: 'neutral',
};

type Loaded<T> = { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: T };

const text = (value: string) => value.trim() || null;

/** Projenin bulgu ve iş görünümü (kaynak yoksa yüklenmez). */
export function useDrydockScope(source: DrydockScopeSource | undefined, projectId: string | null) {
  const [scope, setScope] = useState<Loaded<DrydockScope> | null>(null);
  const reload = useCallback(async () => {
    if (!source || !projectId) {
      setScope(null);
      return;
    }
    try {
      setScope({ kind: 'ready', data: await source.load(projectId) });
    } catch (error) {
      setScope({ kind: 'error', code: errorCode(error) });
    }
  }, [source, projectId]);
  useEffect(() => {
    setScope(source && projectId ? { kind: 'loading' } : null);
    void reload();
  }, [reload, source, projectId]);
  const set = useCallback((data: DrydockScope) => setScope({ kind: 'ready', data }), []);
  return { scope, set, reload };
}

export type ScopeOpen =
  | { kind: 'finding'; id: string }
  | { kind: 'job'; id: string }
  | { kind: 'newFinding'; inspectionId: string; code: string }
  | { kind: 'newJob'; origin?: DrydockOriginInput };

/** Gemide yazılabilir mi (kaynak işlemi, yetki, proje açık; kapalı proje salt okunur, Faz 4). */
const shipWrites = (scope: DrydockScope) =>
  scope.side === 'ship' &&
  scope.project.state !== 'cancelled' &&
  scope.project.state !== 'completed';

/** Kendi kaydı yalnız şirket ayarı açıkken incelenir (sahip kararı 2026-10-26). */
const mayReview = (scope: DrydockScope, createdBy: string) =>
  scope.selfReviewAllowed || scope.viewerId === null || scope.viewerId !== createdBy;

/** Nokta çekmecesindeki bölüm: bu noktanın bulguları ve bu noktadan doğan işler. */
export function CheckpointScope({
  scope,
  code,
  inspectionId,
  canAdd,
  onOpen,
}: {
  scope: DrydockScope;
  code: string;
  inspectionId: string;
  canAdd: boolean;
  onOpen: (open: ScopeOpen) => void;
}) {
  const { t } = useTranslation();
  const findings = scope.findings.filter((f) => f.checkpointCode === code);
  const jobs = scope.jobs.filter((j) => j.origins.some((o) => o.checkpointCode === code));
  const writes = canAdd && shipWrites(scope) && scope.can.create;
  return (
    <Panel title={t('drydock.scope.title')}>
      <Group justify="space-between" gap="xs" mb="xs">
        <p className={classes.muted}>{t('drydock.scope.r4')}</p>
        {writes ? (
          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPlus size={14} aria-hidden />}
              onClick={() => onOpen({ kind: 'newFinding', inspectionId, code })}
            >
              {t('drydock.scope.actions.newFinding')}
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconTool size={14} aria-hidden />}
              onClick={() =>
                onOpen({
                  kind: 'newJob',
                  origin: { type: 'checkpoint', inspectionId, checkpointCode: code },
                })
              }
            >
              {t('drydock.scope.actions.newJob')}
            </Button>
          </Group>
        ) : null}
      </Group>
      {findings.length === 0 && jobs.length === 0 ? (
        <p className={classes.muted}>{t('drydock.scope.noneHere')}</p>
      ) : (
        <ul className={classes.inspections}>
          {findings.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                className={classes.linkButton}
                onClick={() => onOpen({ kind: 'finding', id: f.id })}
              >
                {f.findingNo}
              </button>
              <span>{f.title}</span>
              <StatusBadge tone={FINDING_TONE[f.status]}>
                {t(`drydock.scope.findingStatus.${f.status}`)}
              </StatusBadge>
            </li>
          ))}
          {jobs.map((j) => (
            <li key={j.id}>
              <button
                type="button"
                className={classes.linkButton}
                onClick={() => onOpen({ kind: 'job', id: j.id })}
              >
                {j.jobNo}
              </button>
              <span>{j.title}</span>
              <StatusBadge tone={JOB_TONE[j.status]}>
                {t(`drydock.scope.jobStatus.${j.status}`)}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Projenin bulguları (sekme). */
export function FindingTable({
  rows,
  onOpen,
}: {
  rows: readonly PdiFindingRow[];
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.scope.columns.${key}`);
  if (rows.length === 0) return <EmptyState title={t('drydock.scope.noFindings')} />;
  return (
    <section
      className={table.tableWrap}
      aria-label={t('drydock.scope.findings')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
      tabIndex={0}
    >
      <table className={table.table}>
        <thead>
          <tr>
            <th scope="col">{k('findingNo')}</th>
            <th scope="col">{k('title')}</th>
            <th scope="col">{k('checkpoint')}</th>
            <th scope="col">{k('priority')}</th>
            <th scope="col">{k('disposition')}</th>
            <th scope="col">{k('links')}</th>
            <th scope="col">{k('status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id}>
              <td className={classes.mono}>
                <button type="button" className={classes.linkButton} onClick={() => onOpen(f.id)}>
                  {f.findingNo}
                </button>
              </td>
              <td>{f.title}</td>
              <td>
                <span className={classes.mono}>{f.checkpointCode}</span>
                {f.checkpointName ? ` · ${f.checkpointName}` : ''}
              </td>
              <td>
                <StatusBadge
                  tone={
                    f.priority === 'critical'
                      ? 'danger'
                      : f.priority === 'high'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {t(`drydock.scope.priorities.${f.priority}`)}
                </StatusBadge>
              </td>
              <td>{t(`drydock.scope.dispositions.${f.disposition}`)}</td>
              <td className={classes.mono}>
                {[...f.jobs.map((j) => j.jobNo), ...f.defects.map((d) => d.defectNo)].join(', ') ||
                  '—'}
              </td>
              <td>
                <StatusBadge tone={FINDING_TONE[f.status]}>
                  {t(`drydock.scope.findingStatus.${f.status}`)}
                </StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** İşler (sekme ve Yard Job List). `project`: proje ve gemi sütunu. */
export function JobTable({
  rows,
  onOpen,
  project,
}: {
  rows: readonly (DrydockJobRow & { projectNo?: string; vesselName?: string | null })[];
  onOpen: (row: DrydockJobRow) => void;
  project?: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.scope.columns.${key}`);
  // Faz 4 sütunları: sunucu icra görünümü verdiyse (eski sunucuda yok)
  const execution = rows.some((j) => j.execution !== undefined);
  if (rows.length === 0) return <EmptyState title={t('drydock.scope.noJobs')} />;
  return (
    <section
      className={table.tableWrap}
      aria-label={t('drydock.scope.jobs')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
      tabIndex={0}
    >
      <table className={table.table}>
        <thead>
          <tr>
            <th scope="col">{k('jobNo')}</th>
            {project ? <th scope="col">{k('project')}</th> : null}
            <th scope="col">{k('title')}</th>
            <th scope="col">{k('trade')}</th>
            <th scope="col">{k('quantity')}</th>
            <th scope="col">{k('planned')}</th>
            <th scope="col">{k('origins')}</th>
            <th scope="col">{k('status')}</th>
            {execution ? (
              <>
                <th scope="col">{t('drydock.yard.columns.progress')}</th>
                <th scope="col">{t('drydock.yard.columns.verification')}</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((j) => (
            <tr key={j.id}>
              <td className={classes.mono}>
                <button type="button" className={classes.linkButton} onClick={() => onOpen(j)}>
                  {j.jobNo}
                </button>
              </td>
              {project ? (
                <td>
                  <span className={classes.mono}>{j.projectNo ?? '—'}</span>
                  {j.vesselName ? ` · ${j.vesselName}` : ''}
                </td>
              ) : null}
              <td>{j.title}</td>
              <td>{j.trade ? t(`drydock.scope.trades.${j.trade}`) : t('drydock.scope.tbd')}</td>
              <td className={classes.mono}>
                {j.quantity === null ? '—' : `${j.quantity} ${j.unit ?? ''}`}
              </td>
              <td className={classes.mono}>
                {j.plannedStart || j.plannedEnd
                  ? `${formatDay(j.plannedStart)} – ${formatDay(j.plannedEnd)}`
                  : '—'}
              </td>
              <td className={classes.mono}>
                {j.origins.map((o) => o.findingNo ?? o.checkpointCode).join(', ') || '—'}
              </td>
              <td>
                <StatusBadge tone={JOB_TONE[j.status]}>
                  {t(`drydock.scope.jobStatus.${j.status}`)}
                </StatusBadge>
              </td>
              {execution ? (
                <>
                  <td>
                    <ExecutionBadge job={j} />
                  </td>
                  <td>
                    <VerificationBadge job={j} />
                  </td>
                </>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export type ScopeForm =
  | null
  | 'editFinding'
  | 'reviewFinding'
  | 'cancelFinding'
  | 'defect'
  | 'editJob'
  | 'submitJob'
  | 'cancelJob'
  | 'addOrigin'
  | { voidLink: string }
  | { decide: DrydockDecisionInput['decision'] };

/**
 * Bulgu ve iş çekmeceleri ile pencereleri. `open`: açık kayıt ya da yeni kayıt formu; kayıt
 * sonrası güncel görünüm `onChanged` ile döner.
 */
export function ScopeOverlays({
  scope,
  source,
  open,
  onOpen,
  onClose,
  onChanged,
}: {
  scope: DrydockScope;
  source: DrydockScopeSource;
  open: ScopeOpen | null;
  onOpen: (open: ScopeOpen | null) => void;
  onClose: () => void;
  onChanged: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ScopeForm>(null);
  const [seen, setSeen] = useState(open);
  if (seen !== open) {
    setSeen(open);
    setForm(null);
  }
  const finding =
    open?.kind === 'finding' ? (scope.findings.find((f) => f.id === open.id) ?? null) : null;
  const job = open?.kind === 'job' ? (scope.jobs.find((j) => j.id === open.id) ?? null) : null;
  const saved = (next: DrydockScope) => {
    setForm(null);
    onChanged(next);
  };
  const writes = shipWrites(scope);

  return (
    <>
      <Drawer
        opened={finding !== null}
        onClose={onClose}
        closeButtonProps={{ 'aria-label': t('drydock.close') }}
        position="right"
        size="xl"
        title={finding ? `${finding.findingNo} · ${finding.title}` : ''}
      >
        {finding ? (
          <FindingDetail
            finding={finding}
            scope={scope}
            source={source}
            onForm={setForm}
            onOpen={onOpen}
            onSaved={onChanged}
          />
        ) : null}
      </Drawer>
      <Drawer
        opened={job !== null}
        onClose={onClose}
        closeButtonProps={{ 'aria-label': t('drydock.close') }}
        position="right"
        size="xl"
        title={job ? `${job.jobNo} · ${job.title}` : ''}
      >
        {job ? (
          <JobDetail
            job={job}
            scope={scope}
            source={source}
            onForm={setForm}
            onOpen={onOpen}
            onSaved={onChanged}
          />
        ) : null}
      </Drawer>
      {open?.kind === 'newFinding' && writes && source.saveFinding ? (
        <FindingFormModal
          scope={scope}
          current={null}
          onClose={onClose}
          onSave={(input) =>
            (source.saveFinding as NonNullable<DrydockScopeSource['saveFinding']>)({
              inspectionId: open.inspectionId,
              checkpointCode: open.code,
              finding: input,
            })
          }
          onSaved={(next) => {
            onChanged(next);
            const created = next.findings.at(-1);
            onOpen(created ? { kind: 'finding', id: created.id } : null);
          }}
        />
      ) : null}
      {open?.kind === 'newJob' && writes && source.saveJob ? (
        <JobFormModal
          scope={scope}
          current={null}
          onClose={onClose}
          onSave={(input) =>
            (source.saveJob as NonNullable<DrydockScopeSource['saveJob']>)({
              projectId: scope.project.id,
              ...(open.origin ? { origin: open.origin } : {}),
              job: input,
            })
          }
          onSaved={(next) => {
            onChanged(next);
            const created = next.jobs.at(-1);
            onOpen(created ? { kind: 'job', id: created.id } : null);
          }}
        />
      ) : null}
      {finding && form === 'editFinding' && source.saveFinding ? (
        <FindingFormModal
          scope={scope}
          current={finding}
          onClose={() => setForm(null)}
          onSave={(input) =>
            (source.saveFinding as NonNullable<DrydockScopeSource['saveFinding']>)({
              id: finding.id,
              rowVersion: finding.rowVersion,
              finding: input,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {job && form === 'editJob' && source.saveJob ? (
        <JobFormModal
          scope={scope}
          current={job}
          onClose={() => setForm(null)}
          onSave={(input) =>
            (source.saveJob as NonNullable<DrydockScopeSource['saveJob']>)({
              id: job.id,
              rowVersion: job.rowVersion,
              job: input,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {finding && form === 'reviewFinding' && source.reviewFinding ? (
        <ReviewModal
          title={t('drydock.scope.reviewTitle', { no: finding.findingNo })}
          hint={t('drydock.scope.reviewHint')}
          action={t('drydock.scope.actions.review')}
          self={finding.createdBy === scope.viewerId}
          onClose={() => setForm(null)}
          onSave={(note, body) =>
            (source.reviewFinding as NonNullable<DrydockScopeSource['reviewFinding']>)({
              id: finding.id,
              note,
              ...body,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {job && form === 'submitJob' && source.submitJob ? (
        <ReviewModal
          title={t('drydock.scope.submitTitle', { no: job.jobNo })}
          hint={t('drydock.scope.submitHint')}
          action={t('drydock.scope.actions.submit')}
          self={job.createdBy === scope.viewerId}
          onClose={() => setForm(null)}
          onSave={(note, body) =>
            (source.submitJob as NonNullable<DrydockScopeSource['submitJob']>)({
              id: job.id,
              note,
              ...body,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {finding && form === 'cancelFinding' && source.cancelFinding ? (
        <ReasonModal
          title={t('drydock.scope.cancelFindingTitle', { no: finding.findingNo })}
          action={t('drydock.scope.actions.cancelFinding')}
          danger
          onClose={() => setForm(null)}
          onSave={(reason) =>
            (source.cancelFinding as NonNullable<DrydockScopeSource['cancelFinding']>)({
              id: finding.id,
              rowVersion: finding.rowVersion,
              reason,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {job && form === 'cancelJob' && source.cancelJob ? (
        <ReasonModal
          title={t('drydock.scope.cancelJobTitle', { no: job.jobNo })}
          action={t('drydock.scope.actions.cancelJob')}
          danger
          onClose={() => setForm(null)}
          onSave={(reason) =>
            (source.cancelJob as NonNullable<DrydockScopeSource['cancelJob']>)({
              id: job.id,
              rowVersion: job.rowVersion,
              reason,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {form !== null && typeof form === 'object' && 'voidLink' in form && source.voidLink ? (
        <ReasonModal
          title={t('drydock.scope.voidLinkTitle')}
          action={t('drydock.scope.actions.voidLink')}
          danger
          onClose={() => setForm(null)}
          onSave={(reason) =>
            (source.voidLink as NonNullable<DrydockScopeSource['voidLink']>)({
              id: form.voidLink,
              reason,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {finding && form === 'defect' ? (
        <DefectModal
          finding={finding}
          scope={scope}
          source={source}
          onClose={() => setForm(null)}
          onSaved={saved}
        />
      ) : null}
      {job && form === 'addOrigin' && source.link ? (
        <AddOriginModal
          job={job}
          scope={scope}
          source={source}
          onClose={() => setForm(null)}
          onSaved={saved}
        />
      ) : null}
      {job && form !== null && typeof form === 'object' && 'decide' in form && source.decide ? (
        <DecisionModal
          job={job}
          decision={form.decide}
          source={source}
          onClose={() => setForm(null)}
          onSaved={saved}
        />
      ) : null}
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function ReviewList({
  rows,
  kind,
}: {
  rows: readonly DrydockReviewRow[];
  kind: 'finding' | 'job';
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <p className={classes.muted}>
        {t(kind === 'finding' ? 'drydock.scope.noReviews' : 'drydock.scope.noSubmissions')}
      </p>
    );
  }
  return (
    <ul className={classes.inspections}>
      {[...rows].reverse().map((r) => (
        <li key={r.id}>
          <span className={classes.mono}>{t('drydock.work.revision', { no: r.revision })}</span>
          <span className={classes.muted}>
            {formatDateTime(r.createdAt)} · {r.reviewerName ?? '—'}
            {r.selfReview ? ` · ${t('drydock.scope.selfReview')}` : ''}
            {r.note ? ` · ${r.note}` : ''}
          </span>
          {r.decision ? (
            <span>
              <StatusBadge tone={JOB_TONE[r.decision.decision]}>
                {t(`drydock.scope.jobStatus.${r.decision.decision}`)}
              </StatusBadge>{' '}
              <span className={classes.muted}>
                {r.decision.deciderName ?? '—'} · {formatDateTime(r.decision.createdAt)}
                {r.decision.reason ? ` · ${r.decision.reason}` : ''}
              </span>
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function FindingDetail({
  finding,
  scope,
  source,
  onForm,
  onOpen,
  onSaved,
}: {
  finding: PdiFindingRow;
  scope: DrydockScope;
  source: DrydockScopeSource;
  onForm: (form: ScopeForm) => void;
  onOpen: (open: ScopeOpen) => void;
  onSaved?: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drydock.scope.fields.${key}`);
  const open = finding.state === 'open' && shipWrites(scope);
  const can = scope.can;
  const own = !mayReview(scope, finding.createdBy);
  return (
    <div className={classes.detail}>
      <p className={classes.path}>
        {finding.inspectionNo} · {finding.checkpointCode}
        {finding.checkpointName ? ` · ${finding.checkpointName}` : ''}
      </p>
      <Group gap="xs">
        <StatusBadge tone={FINDING_TONE[finding.status]}>
          {t(`drydock.scope.findingStatus.${finding.status}`)}
        </StatusBadge>
        <StatusBadge tone={finding.priority === 'critical' ? 'danger' : 'neutral'}>
          {t(`drydock.scope.priorities.${finding.priority}`)}
        </StatusBadge>
        {finding.safetyCritical ? (
          <StatusBadge tone="danger">{f('safetyCritical')}</StatusBadge>
        ) : null}
        {finding.changed ? (
          <StatusBadge tone="warning">{t('drydock.scope.changedSinceReview')}</StatusBadge>
        ) : null}
      </Group>
      <Group justify="flex-end" gap="xs">
        {open && can.edit && source.saveFinding ? (
          <Button
            variant="default"
            leftSection={<IconEdit size={16} aria-hidden />}
            onClick={() => onForm('editFinding')}
          >
            {t('drydock.scope.actions.edit')}
          </Button>
        ) : null}
        {open && can.void && source.cancelFinding ? (
          <Button
            variant="default"
            color="red"
            leftSection={<IconBan size={16} aria-hidden />}
            onClick={() => onForm('cancelFinding')}
          >
            {t('drydock.scope.actions.cancelFinding')}
          </Button>
        ) : null}
        {open && can.create && source.saveJob ? (
          <Button
            variant="default"
            leftSection={<IconTool size={16} aria-hidden />}
            onClick={() =>
              onOpen({ kind: 'newJob', origin: { type: 'finding', findingId: finding.id } })
            }
          >
            {t('drydock.scope.actions.newJobFromFinding')}
          </Button>
        ) : null}
        {open && can.create && (source.link || source.openDefect) ? (
          <Button
            variant="default"
            leftSection={<IconLink size={16} aria-hidden />}
            onClick={() => onForm('defect')}
          >
            {t('drydock.scope.actions.defect')}
          </Button>
        ) : null}
        {open && can.review && source.reviewFinding && finding.status === 'draft' ? (
          <Button
            leftSection={<IconEye size={16} aria-hidden />}
            disabled={own}
            title={own ? t('drydock.scope.ownRecord') : undefined}
            onClick={() => onForm('reviewFinding')}
          >
            {t('drydock.scope.actions.review')}
          </Button>
        ) : null}
      </Group>
      <dl className={classes.fields}>
        <Field label={f('description')}>{finding.description}</Field>
        <Field label={f('location')}>{finding.location}</Field>
        <Field label={f('equipment')}>{finding.equipmentName ?? '—'}</Field>
        <Field label={f('disposition')}>
          {t(`drydock.scope.dispositions.${finding.disposition}`)}
        </Field>
        <Field label={f('immediateAction')}>{finding.immediateAction ?? '—'}</Field>
        <Field label={f('createdBy')}>
          {finding.createdByName ?? '—'} · {formatDateTime(finding.createdAt)}
        </Field>
        {finding.cancelReason ? (
          <Field label={f('cancelReason')}>{finding.cancelReason}</Field>
        ) : null}
      </dl>
      <Panel title={t('drydock.scope.linkedJobs')}>
        {finding.jobs.length === 0 ? (
          <p className={classes.muted}>{t('drydock.scope.noJobs')}</p>
        ) : (
          <ul className={classes.inspections}>
            {finding.jobs.map((j) => (
              <li key={j.linkId}>
                <button
                  type="button"
                  className={classes.linkButton}
                  onClick={() => onOpen({ kind: 'job', id: j.jobId })}
                >
                  {j.jobNo}
                </button>
                <span>{j.title}</span>
                <StatusBadge tone={JOB_TONE[j.status]}>
                  {t(`drydock.scope.jobStatus.${j.status}`)}
                </StatusBadge>
                {open && can.void && source.voidLink ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() => onForm({ voidLink: j.linkId })}
                  >
                    {t('drydock.scope.actions.voidLink')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title={t('drydock.scope.linkedDefects')}>
        {finding.defects.length === 0 ? (
          <p className={classes.muted}>{t('drydock.scope.noDefects')}</p>
        ) : (
          <ul className={classes.inspections}>
            {finding.defects.map((d) => (
              <li key={d.linkId}>
                <span className={classes.mono}>{d.defectNo}</span>
                <span>{d.description}</span>
                <StatusBadge tone={d.closed ? 'success' : 'warning'}>
                  {t(d.closed ? 'drydock.scope.defectClosed' : 'drydock.scope.defectOpen')}
                </StatusBadge>
                <span className={classes.muted}>{t(`drydock.scope.relations.${d.relation}`)}</span>
                {open && can.void && source.voidLink ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() => onForm({ voidLink: d.linkId })}
                  >
                    {t('drydock.scope.actions.voidLink')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {onSaved ? (
        <FindingClosurePanel finding={finding} scope={scope} source={source} onSaved={onSaved} />
      ) : null}
      <Panel title={t('drydock.scope.reviews')}>
        <ReviewList rows={finding.reviews} kind="finding" />
      </Panel>
    </div>
  );
}

export function JobDetail({
  job,
  scope,
  source,
  onForm,
  onOpen,
  onSaved,
}: {
  job: DrydockJobRow;
  scope: DrydockScope;
  source: DrydockScopeSource;
  onForm: (form: ScopeForm) => void;
  onOpen: (open: ScopeOpen) => void;
  onSaved?: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drydock.scope.fields.${key}`);
  const open = job.state === 'open' && shipWrites(scope);
  const can = scope.can;
  const own = !mayReview(scope, job.createdBy);
  const office = scope.side === 'office' && job.status === 'for_review' && Boolean(source.decide);
  return (
    <div className={classes.detail}>
      <p className={classes.path}>
        {scope.project.projectNo} · {scope.project.title}
        {scope.project.vesselName ? ` · ${scope.project.vesselName}` : ''}
      </p>
      <Group gap="xs">
        <StatusBadge tone={JOB_TONE[job.status]}>
          {t(`drydock.scope.jobStatus.${job.status}`)}
        </StatusBadge>
        {job.changed !== 'none' ? (
          <StatusBadge tone="warning">{t(`drydock.scope.changed.${job.changed}`)}</StatusBadge>
        ) : null}
      </Group>
      <Group justify="flex-end" gap="xs">
        {open && can.edit && source.saveJob ? (
          <Button
            variant="default"
            leftSection={<IconEdit size={16} aria-hidden />}
            onClick={() => onForm('editJob')}
          >
            {t('drydock.scope.actions.edit')}
          </Button>
        ) : null}
        {open && can.void && source.cancelJob ? (
          <Button
            variant="default"
            color="red"
            leftSection={<IconBan size={16} aria-hidden />}
            onClick={() => onForm('cancelJob')}
          >
            {t('drydock.scope.actions.cancelJob')}
          </Button>
        ) : null}
        {open && can.create && source.link ? (
          <Button
            variant="default"
            leftSection={<IconLink size={16} aria-hidden />}
            onClick={() => onForm('addOrigin')}
          >
            {t('drydock.scope.actions.addOrigin')}
          </Button>
        ) : null}
        {open && can.review && source.submitJob && job.status === 'draft' ? (
          <Button
            leftSection={<IconSend size={16} aria-hidden />}
            disabled={own}
            title={own ? t('drydock.scope.ownRecord') : undefined}
            onClick={() => onForm('submitJob')}
          >
            {t('drydock.scope.actions.submit')}
          </Button>
        ) : null}
        {office && can.return ? (
          <>
            <Button
              variant="default"
              color="red"
              leftSection={<IconX size={16} aria-hidden />}
              onClick={() => onForm({ decide: 'rejected' })}
            >
              {t('drydock.scope.actions.reject')}
            </Button>
            <Button variant="default" onClick={() => onForm({ decide: 'returned' })}>
              {t('drydock.scope.actions.return')}
            </Button>
          </>
        ) : null}
        {office && can.decide ? (
          <Button
            leftSection={<IconCheck size={16} aria-hidden />}
            onClick={() => onForm({ decide: 'approved' })}
          >
            {t('drydock.scope.actions.approve')}
          </Button>
        ) : null}
      </Group>
      <dl className={classes.fields}>
        <Field label={f('scope')}>{job.scope}</Field>
        <Field label={f('trade')}>
          {job.trade ? t(`drydock.scope.trades.${job.trade}`) : t('drydock.scope.tbd')}
        </Field>
        <Field label={f('contractor')}>{job.contractor ?? '—'}</Field>
        <Field label={f('quantity')}>
          {job.quantity === null ? '—' : `${job.quantity} ${job.unit ?? ''}`}
        </Field>
        <Field label={f('planned')}>
          {job.plannedStart || job.plannedEnd
            ? `${formatDay(job.plannedStart)} – ${formatDay(job.plannedEnd)}`
            : '—'}
        </Field>
        <Field label={f('equipment')}>{job.equipmentName ?? '—'}</Field>
        <Field label={f('createdBy')}>
          {job.createdByName ?? '—'} · {formatDateTime(job.createdAt)}
        </Field>
        {job.cancelReason ? <Field label={f('cancelReason')}>{job.cancelReason}</Field> : null}
      </dl>
      <Panel title={t('drydock.scope.origins')}>
        <ul className={classes.inspections}>
          {job.origins.map((o) => (
            <li key={o.linkId}>
              {o.type === 'finding' && o.findingId ? (
                <button
                  type="button"
                  className={classes.linkButton}
                  onClick={() => onOpen({ kind: 'finding', id: o.findingId as string })}
                >
                  {o.findingNo}
                </button>
              ) : (
                <span className={classes.mono}>{o.checkpointCode}</span>
              )}
              <span>{o.checkpointName ?? o.checkpointCode}</span>
              <span className={classes.muted}>{t(`drydock.scope.relations.${o.relation}`)}</span>
              {open && can.void && source.voidLink && job.origins.length > 1 ? (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => onForm({ voidLink: o.linkId })}
                >
                  {t('drydock.scope.actions.voidLink')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>
      {onSaved && job.execution ? (
        <JobExecutionPanel job={job} scope={scope} source={source} onSaved={onSaved} />
      ) : null}
      <Panel title={t('drydock.scope.submissions')}>
        <ReviewList rows={job.submissions} kind="job" />
      </Panel>
    </div>
  );
}

/** Kaydet düğmeli pencere iskeleti: hata metni ve meşgul durumu. */
function useSave<T>(save: () => Promise<T>, onSaved: (value: T) => void) {
  const errorText = useDrydockErrorText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await save());
    } catch (err) {
      setError(errorText(err));
      return err;
    } finally {
      setBusy(false);
    }
    return null;
  }
  return { busy, error, run, setError };
}

function equipmentData(scope: DrydockScope) {
  return scope.equipment.map((e) => ({
    value: e.id,
    label: e.code ? `${e.code} ${e.name}` : e.name,
  }));
}

function FindingFormModal({
  scope,
  current,
  onClose,
  onSave,
  onSaved,
}: {
  scope: DrydockScope;
  current: PdiFindingRow | null;
  onClose: () => void;
  onSave: (input: PdiFindingInput) => Promise<DrydockScope>;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drydock.scope.fields.${key}`);
  const [value, setValue] = useState<PdiFindingInput>(
    current
      ? {
          title: current.title,
          description: current.description,
          location: current.location,
          equipmentId: current.equipmentId,
          priority: current.priority,
          safetyCritical: current.safetyCritical,
          immediateAction: current.immediateAction,
          disposition: current.disposition,
        }
      : {
          title: '',
          description: '',
          location: '',
          equipmentId: null,
          priority: 'medium',
          safetyCritical: false,
          immediateAction: null,
          disposition: 'dry_dock',
        },
  );
  const set = (patch: Partial<PdiFindingInput>) => setValue((v) => ({ ...v, ...patch }));
  const needsAction = value.priority === 'critical' || value.safetyCritical;
  const { busy, error, run } = useSave(
    () => onSave({ ...value, immediateAction: text(value.immediateAction ?? '') }),
    onSaved,
  );
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={
        current
          ? t('drydock.scope.editFindingTitle', { no: current.findingNo })
          : t('drydock.scope.newFindingTitle')
      }
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        {current && current.reviews.length > 0 ? (
          <Alert color="yellow">{t('drydock.scope.editReviewedHint')}</Alert>
        ) : null}
        {error ? <Alert color="red">{error}</Alert> : null}
        <TextInput
          label={f('title')}
          required
          maxLength={200}
          value={value.title}
          onChange={(e) => set({ title: e.currentTarget.value })}
        />
        <Textarea
          label={f('description')}
          required
          autosize
          minRows={3}
          maxLength={4000}
          value={value.description}
          onChange={(e) => set({ description: e.currentTarget.value })}
        />
        <div className={classes.formGrid}>
          <TextInput
            label={f('location')}
            required
            maxLength={200}
            value={value.location}
            onChange={(e) => set({ location: e.currentTarget.value })}
          />
          <Select
            label={f('equipment')}
            placeholder={t('drydock.resultFields.noEquipment')}
            data={equipmentData(scope)}
            value={value.equipmentId}
            clearable
            searchable
            onChange={(v) => set({ equipmentId: v })}
          />
          <Select
            label={f('priority')}
            required
            data={PDI_FINDING_PRIORITIES.map((p) => ({
              value: p,
              label: t(`drydock.scope.priorities.${p}`),
            }))}
            value={value.priority}
            allowDeselect={false}
            onChange={(v) => v && set({ priority: v as PdiFindingInput['priority'] })}
          />
          <Select
            label={f('disposition')}
            required
            data={PDI_DISPOSITIONS.map((d) => ({
              value: d,
              label: t(`drydock.scope.dispositions.${d}`),
            }))}
            value={value.disposition}
            allowDeselect={false}
            onChange={(v) => v && set({ disposition: v as PdiFindingInput['disposition'] })}
          />
        </div>
        <Checkbox
          label={f('safetyCritical')}
          checked={value.safetyCritical}
          onChange={(e) => set({ safetyCritical: e.currentTarget.checked })}
        />
        <Textarea
          label={f('immediateAction')}
          description={needsAction ? t('drydock.scope.immediateActionHint') : undefined}
          required={needsAction}
          autosize
          minRows={2}
          maxLength={2000}
          value={value.immediateAction ?? ''}
          onChange={(e) => set({ immediateAction: e.currentTarget.value })}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} onClick={() => void run()}>
            {t('drydock.save')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

function JobFormModal({
  scope,
  current,
  onClose,
  onSave,
  onSaved,
}: {
  scope: DrydockScope;
  current: DrydockJobRow | null;
  onClose: () => void;
  onSave: (input: DrydockJobInput) => Promise<DrydockScope>;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drydock.scope.fields.${key}`);
  const [value, setValue] = useState<DrydockJobInput>(
    current
      ? {
          title: current.title,
          scope: current.scope,
          trade: current.trade,
          contractor: current.contractor,
          quantity: current.quantity,
          unit: current.unit,
          plannedStart: current.plannedStart,
          plannedEnd: current.plannedEnd,
          equipmentId: current.equipmentId,
        }
      : {
          title: '',
          scope: '',
          trade: null,
          contractor: null,
          quantity: null,
          unit: null,
          plannedStart: null,
          plannedEnd: null,
          equipmentId: null,
        },
  );
  const set = (patch: Partial<DrydockJobInput>) => setValue((v) => ({ ...v, ...patch }));
  const { busy, error, run } = useSave(
    () =>
      onSave({
        ...value,
        contractor: text(value.contractor ?? ''),
        unit: text(value.unit ?? ''),
        plannedStart: value.plannedStart || null,
        plannedEnd: value.plannedEnd || null,
      }),
    onSaved,
  );
  const approved = current !== null && current.status !== 'draft';
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={
        current
          ? t('drydock.scope.editJobTitle', { no: current.jobNo })
          : t('drydock.scope.newJobTitle')
      }
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        {approved ? <Alert color="yellow">{t('drydock.scope.editSubmittedHint')}</Alert> : null}
        {error ? <Alert color="red">{error}</Alert> : null}
        <TextInput
          label={f('title')}
          required
          maxLength={200}
          value={value.title}
          onChange={(e) => set({ title: e.currentTarget.value })}
        />
        <Textarea
          label={f('scope')}
          required
          autosize
          minRows={3}
          maxLength={8000}
          value={value.scope}
          onChange={(e) => set({ scope: e.currentTarget.value })}
        />
        <div className={classes.formGrid}>
          <Select
            label={f('trade')}
            placeholder={t('drydock.scope.tbd')}
            data={DRYDOCK_TRADES.map((d) => ({ value: d, label: t(`drydock.scope.trades.${d}`) }))}
            value={value.trade}
            clearable
            onChange={(v) => set({ trade: (v as DrydockJobInput['trade']) ?? null })}
          />
          <TextInput
            label={f('contractor')}
            maxLength={200}
            value={value.contractor ?? ''}
            onChange={(e) => set({ contractor: e.currentTarget.value })}
          />
          <NumberInput
            label={f('quantity')}
            min={0}
            decimalScale={3}
            value={value.quantity ?? ''}
            onChange={(v) => set({ quantity: typeof v === 'number' ? v : null })}
          />
          <TextInput
            label={f('unit')}
            maxLength={40}
            value={value.unit ?? ''}
            onChange={(e) => set({ unit: e.currentTarget.value })}
          />
          <TextInput
            type="date"
            label={f('plannedStart')}
            value={value.plannedStart ?? ''}
            onChange={(e) => set({ plannedStart: e.currentTarget.value })}
          />
          <TextInput
            type="date"
            label={f('plannedEnd')}
            value={value.plannedEnd ?? ''}
            onChange={(e) => set({ plannedEnd: e.currentTarget.value })}
          />
          <Select
            label={f('equipment')}
            placeholder={t('drydock.resultFields.noEquipment')}
            data={equipmentData(scope)}
            value={value.equipmentId}
            clearable
            searchable
            onChange={(v) => set({ equipmentId: v })}
          />
        </div>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} onClick={() => void run()}>
            {t('drydock.save')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Teknik inceleme ya da ofise gönderim: not ve (ayar açıksa) parola. */
function ReviewModal({
  title,
  hint,
  action,
  self,
  onClose,
  onSave,
  onSaved,
}: {
  title: string;
  hint: string;
  action: string;
  self: boolean;
  onClose: () => void;
  onSave: (note: string | null, body: { password?: string }) => Promise<DrydockScope>;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const reauth = useReauth('drydock_review');
  const [note, setNote] = useState('');
  const { busy, error, run } = useSave(() => onSave(text(note), reauth.body()), onSaved);
  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{hint}</p>
        {self ? <Alert color="yellow">{t('drydock.scope.selfReviewHint')}</Alert> : null}
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t('drydock.scope.fields.note')}
          autosize
          minRows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <ReauthField reauth={reauth} label={t('drydock.scope.password')} />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={reauth.missing}
            onClick={() =>
              void run().then((err) => {
                if (err) reauth.noteError(err);
              })
            }
          >
            {action}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Gerekçeli işlem (iptal, bağ iptali). */
function ReasonModal({
  title,
  action,
  danger,
  onClose,
  onSave,
  onSaved,
}: {
  title: string;
  action: string;
  danger?: boolean;
  onClose: () => void;
  onSave: (reason: string) => Promise<DrydockScope>;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const { busy, error, run } = useSave(() => onSave(reason.trim()), onSaved);
  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t('drydock.scope.fields.reason')}
          required
          autosize
          minRows={2}
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button
            {...(danger ? { color: 'red' } : {})}
            loading={busy}
            disabled={reason.trim() === ''}
            onClick={() => void run()}
          >
            {action}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

const SEVERITY = { critical: 'critical', high: 'major', medium: 'minor', low: 'minor' } as const;

/**
 * Arıza (Excel Screen 7 "Search existing defects first"; R16): önce açık arızalar aranır ve
 * bağlanır; yoksa arıza modülünden yeni arıza açılır. Bulgu her zaman arıza değildir.
 */
function DefectModal({
  finding,
  scope,
  source,
  onClose,
  onSaved,
}: {
  finding: PdiFindingRow;
  scope: DrydockScope;
  source: DrydockScopeSource;
  onClose: () => void;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<PdiDefectCandidate[] | null>(null);
  const [severity, setSeverity] = useState<'minor' | 'major' | 'critical'>(
    SEVERITY[finding.priority],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const linked = useMemo(() => new Set(finding.defects.map((d) => d.defectId)), [finding.defects]);
  const created = finding.defects.some((d) => d.relation === 'originates');

  const search = useCallback(async () => {
    if (!source.defects) {
      setRows([]);
      return;
    }
    try {
      const found = await source.defects({ equipmentId: finding.equipmentId, q: q.trim() });
      setRows(found.defects);
    } catch (err) {
      setError(errorText(err));
      setRows([]);
    }
  }, [source, finding.equipmentId, q, errorText]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: ilk açılışta bir kez aranır
  useEffect(() => {
    void search();
  }, []);

  async function act(work: () => Promise<DrydockScope>) {
    setBusy(true);
    setError(null);
    try {
      onSaved(await work());
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={t('drydock.scope.defectTitle', { no: finding.findingNo })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t('drydock.scope.defectHint')}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Group gap="xs" align="flex-end">
          <TextInput
            style={{ flex: 1 }}
            label={t('drydock.scope.defectSearch')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void search();
            }}
          />
          <Button variant="default" onClick={() => void search()}>
            {t('drydock.scope.actions.search')}
          </Button>
        </Group>
        {rows === null ? null : rows.length === 0 ? (
          <p className={classes.muted}>{t('drydock.scope.noOpenDefects')}</p>
        ) : (
          <ul className={classes.inspections}>
            {rows.map((d) => (
              <li key={d.id}>
                <span className={classes.mono}>{d.defectNo}</span>
                <span>
                  {d.description}
                  {d.equipmentName ? ` · ${d.equipmentName}` : ''}
                </span>
                {linked.has(d.id) ? (
                  <StatusBadge tone="neutral">{t('drydock.scope.alreadyLinked')}</StatusBadge>
                ) : source.link ? (
                  <Button
                    size="compact-xs"
                    variant="default"
                    disabled={busy}
                    onClick={() =>
                      void act(() =>
                        (source.link as NonNullable<DrydockScopeSource['link']>)({
                          projectId: scope.project.id,
                          link: {
                            sourceType: 'finding',
                            sourceId: finding.id,
                            targetType: 'defect',
                            targetId: d.id,
                            relation: 'related',
                          },
                        }),
                      )
                    }
                  >
                    {t('drydock.scope.actions.linkDefect')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {source.openDefect && scope.can.defect && !created ? (
          <Panel title={t('drydock.scope.createDefect')}>
            <Group gap="xs" align="flex-end">
              <Select
                label={t('drydock.scope.fields.severity')}
                data={(['minor', 'major', 'critical'] as const).map((s) => ({
                  value: s,
                  label: t(`drydock.scope.severities.${s}`),
                }))}
                value={severity}
                allowDeselect={false}
                onChange={(v) => v && setSeverity(v as typeof severity)}
              />
              <Button
                loading={busy}
                onClick={() =>
                  void act(() =>
                    (source.openDefect as NonNullable<DrydockScopeSource['openDefect']>)({
                      findingId: finding.id,
                      severity,
                    }),
                  )
                }
              >
                {t('drydock.scope.actions.createDefect')}
              </Button>
            </Group>
          </Panel>
        ) : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('drydock.close')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** İşe kaynak ekler: projenin bir bulgusu ya da denetimin bir kontrol noktası (R3). */
function AddOriginModal({
  job,
  scope,
  source,
  onClose,
  onSaved,
}: {
  job: DrydockJobRow;
  scope: DrydockScope;
  source: DrydockScopeSource;
  onClose: () => void;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'finding' | 'checkpoint'>('finding');
  const used = new Set(job.origins.map((o) => o.findingId).filter(Boolean));
  const findings = scope.findings.filter((f) => f.state === 'open' && !used.has(f.id));
  const [findingId, setFindingId] = useState<string | null>(findings[0]?.id ?? null);
  const [inspectionId, setInspectionId] = useState<string | null>(
    scope.inspections.find((i) => i.state === 'open')?.id ?? scope.inspections[0]?.id ?? null,
  );
  const [code, setCode] = useState('');
  const { busy, error, run } = useSave(
    () =>
      (source.link as NonNullable<DrydockScopeSource['link']>)({
        projectId: scope.project.id,
        link:
          kind === 'finding'
            ? {
                sourceType: 'finding',
                sourceId: findingId,
                targetType: 'job',
                targetId: job.id,
                relation: 'addresses',
              }
            : {
                sourceType: 'checkpoint',
                inspectionId,
                checkpointCode: code.trim(),
                targetType: 'job',
                targetId: job.id,
                relation: 'originates',
              },
      }),
    onSaved,
  );
  return (
    <Modal
      opened
      onClose={onClose}
      title={t('drydock.scope.addOriginTitle', { no: job.jobNo })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t('drydock.scope.addOriginHint')}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Select
          label={t('drydock.scope.fields.originType')}
          data={[
            { value: 'finding', label: t('drydock.scope.originTypes.finding') },
            { value: 'checkpoint', label: t('drydock.scope.originTypes.checkpoint') },
          ]}
          value={kind}
          allowDeselect={false}
          onChange={(v) => v && setKind(v as typeof kind)}
        />
        {kind === 'finding' ? (
          <Select
            label={t('drydock.scope.fields.finding')}
            data={findings.map((f) => ({ value: f.id, label: `${f.findingNo} · ${f.title}` }))}
            value={findingId}
            searchable
            onChange={setFindingId}
          />
        ) : (
          <>
            <Select
              label={t('drydock.work.inspection')}
              data={scope.inspections.map((i) => ({ value: i.id, label: i.inspectionNo }))}
              value={inspectionId}
              allowDeselect={false}
              onChange={setInspectionId}
            />
            <TextInput
              label={t('drydock.scope.fields.checkpointCode')}
              placeholder="PDI-01-01-001"
              maxLength={40}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
            />
          </>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={kind === 'finding' ? !findingId : !inspectionId || code.trim() === ''}
            onClick={() => void run()}
          >
            {t('drydock.scope.actions.addOrigin')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Ofis kararı: onay ya da gerekçeli iade / ret; içerik hash'i gönderimin hash'i. */
function DecisionModal({
  job,
  decision,
  source,
  onClose,
  onSaved,
}: {
  job: DrydockJobRow;
  decision: DrydockDecisionInput['decision'];
  source: DrydockScopeSource;
  onClose: () => void;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const reauth = useReauth('drydock_job_decision');
  const [reason, setReason] = useState('');
  const needsReason = decision !== 'approved';
  const hash = job.submissions.at(-1)?.contentHash ?? job.contentHash;
  const { busy, error, run } = useSave(
    () =>
      (source.decide as NonNullable<DrydockScopeSource['decide']>)({
        jobId: job.id,
        decision: { decision, reason: text(reason), contentHash: hash },
        ...reauth.body(),
      }),
    onSaved,
  );
  return (
    <Modal
      opened
      onClose={onClose}
      title={t(`drydock.scope.decisionTitle.${decision}`, { no: job.jobNo })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t(`drydock.scope.decisionHint.${decision}`)}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t('drydock.scope.fields.reason')}
          required={needsReason}
          autosize
          minRows={2}
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <ReauthField reauth={reauth} label={t('drydock.scope.password')} />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button
            {...(decision === 'approved' ? {} : { color: 'red' })}
            loading={busy}
            disabled={reauth.missing || (needsReason && reason.trim() === '')}
            onClick={() =>
              void run().then((err) => {
                if (err) reauth.noteError(err);
              })
            }
          >
            {t(
              `drydock.scope.actions.${decision === 'approved' ? 'approve' : decision === 'returned' ? 'return' : 'reject'}`,
            )}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}
